import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { CircularProgress, TextField } from '@material-ui/core';
import { TextFieldProps } from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import useDebounce from '../../../hooks/useDebounce';
import { ALL_DEVICES } from '../../../graphql/queries/Devices';
import { AvailableHubOption, DeviceType } from '../../../types/Devices';

type Props = {
  onHubChange: (hub: AvailableHubOption | null) => void;
  value?: AvailableHubOption | null;
} & TextFieldProps;

const AvailableHubSearch: React.FC<Props> = ({
  onHubChange,
  value,
  ...textFieldProps
}) => {
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, loading } = useQuery<{ hubs: Paginated<DeviceType> }>(ALL_DEVICES, {
    variables: {
      limit: 20,
      offset: 0,
      search: debouncedSearch,
      sortBy: 'serial_number',
      direction: 'ASC',
      onlyUnassigned: true,
    },
    fetchPolicy: 'network-only',
  });

  const options = useMemo<AvailableHubOption[]>(
    () => (data?.hubs?.list || []).map(hub => ({
      id: hub.id,
      serial_number: hub.serial_number,
      description: hub.description,
      imei: hub.imei,
    })),
    [data],
  );

  const selectedValue = useMemo(() => {
    if (!value?.serial_number) {
      return null;
    }

    return options.find(option => option.id === value.id || option.serial_number === value.serial_number)
      || value;
  }, [options, value]);

  return (
    <Autocomplete
      options={options}
      value={selectedValue}
      getOptionLabel={option => option.serial_number || ''}
      noOptionsText="No unassigned serial numbers were found"
      loading={loading}
      onChange={(_event: React.ChangeEvent<{}>, selectedOption: AvailableHubOption | null) => onHubChange(selectedOption)}
      onInputChange={(_event: React.ChangeEvent<{}>, newInputValue: string, reason: string) => {
        if (reason === 'input') {
          setSearch(newInputValue);
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label="Serial number"
          fullWidth
          margin="normal"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default React.memo(AvailableHubSearch);
